import { useEffect, useMemo, useState } from "react";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import NewDateField from "../../../DesignSystem/Inputs/NewDateField";
import NewInputField from "../../../DesignSystem/Inputs/NewInputField";
import NewTextArea from "../../../DesignSystem/Inputs/NewTextArea";
import RadioGroup from "../../../DesignSystem/Inputs/RadioGroup";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import { createManualPayment, deleteManualPayment, updateManualPayment } from "../services/manualPaymentService";
import { useUserPermissions } from "../../../SignIn/UserPermissionsContext";
import { getDepartmentConfig } from "../../../Departments/utils/departmentConfig";
import { APP_ROUTES } from "../../../../constants/appRoutes";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/close.svg";

const AddPayment = ({
  adviser,
  payment,
  initialType = "additional_payment",
  onClose,
  asPage = false,
  defaultDate,
}) => {
  const { userPermissions } = useUserPermissions();
  const canSeeAdvance = useMemo(() => {
    const { Finance } = getDepartmentConfig(
      Array.isArray(userPermissions) ? userPermissions : [],
    );
    return (
      Finance.show &&
      Finance.cards.some((card) => card.route === APP_ROUTES.MONTHLY_PAYMENTS)
    );
  }, [userPermissions]);
  const [paymentType, setPaymentType] = useState(payment?.paymentType || initialType);
  const [form, setForm] = useState({
    date: String(payment?.date || defaultDate || new Date().toISOString()).slice(0, 10),
    description: payment?.description || "",
    amount: payment?.amountPaid == null
      ? ""
      : String(
          (payment.paymentType || initialType) === "additional_payment"
            ? Math.abs(Number(payment.amountPaid || 0))
            : payment.amountPaid,
        ),
    notes: payment?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const update = (key) => (event) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));
  useEffect(() => {
    if (!canSeeAdvance && paymentType === "advance") {
      setPaymentType("additional_payment");
    }
  }, [canSeeAdvance, paymentType]);
  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      const values = {
        ...form,
        paymentType,
        adviserEmail: adviser.email,
        adviserName: adviser.name,
        adviserId: adviser.id,
        brandName: adviser.brand,
      };
      if (payment?.id) await updateManualPayment({ paymentId: payment.id, ...values });
      else await createManualPayment(values);
      onClose(paymentType);
    } catch (saveError) {
      setError(saveError.message || "The payment could not be saved.");
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!payment?.id) return onClose(paymentType);
    setSaving(true);
    try {
      await deleteManualPayment(payment.id);
      onClose(paymentType);
    } catch (deleteError) {
      setError(deleteError.message || "The payment could not be removed.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <form
      className={`ParaplanningModal ${asPage ? "add-charge-page-card" : "add-payment-modal"}`}
      role={asPage ? undefined : "dialog"}
      aria-modal={asPage ? undefined : "true"}
      aria-label={payment?.id ? "Edit payment" : "Add payment"}
      onSubmit={submit}
      onMouseDown={(event) => event.stopPropagation()}
    >
      {!asPage ? (
        <div className="ParaplanningModal__Header">
          <h2 className="FP_H3">{payment?.id ? "Edit Payment" : "Add Payment"}</h2>
          <button type="button" aria-label="Close" onClick={() => onClose(paymentType)}>
            <CloseIcon />
          </button>
        </div>
      ) : null}
      <div className="ParaplanningModal__Grid">
        <CustomSelect
          label="Adviser Name"
          value={adviser.email}
          selectedLabel={adviser.name}
          options={[{ id: adviser.email, name: adviser.name }]}
          disabled
          searchable={false}
          clearable={false}
        />
        <NewDateField
          label="Date"
          value={form.date}
          onChange={update("date")}
          required
        />
      </div>
      <RadioGroup
        name="manual-payment-type"
        options={[
          { value: "additional_payment", label: "Additional Payments" },
          ...(canSeeAdvance ? [{ value: "advance", label: "Advances" }] : []),
        ]}
        value={paymentType}
        onChange={(event) => setPaymentType(event.target.value)}
        inline
      />
      <NewTextArea
        id="payment-description"
        label="Payment Description"
        value={form.description}
        onChange={update("description")}
        placeholder="Details/reason of payment"
      />
      <NewInputField
        id="payment-amount"
        label="Total Amount (£)"
        type="number"
        value={form.amount}
        onChange={update("amount")}
        placeholder="Amount to pay"
        required
      />
      <NewTextArea
        id="payment-notes"
        label="Additional Notes"
        value={form.notes}
        onChange={update("notes")}
        placeholder="Additional notes"
      />
      {error ? <p className="FP_Content text-error">{error}</p> : null}
      <div className="ParaplanningModal__Actions">
        <SecondaryButton
          type="button"
          label="Remove Payment"
          onClick={remove}
          disabled={saving}
        />
        <PrimaryButton
          type="submit"
          label={saving ? "Saving…" : payment?.id ? "Save Changes" : "Add Payment"}
          disabled={saving}
        />
      </div>
    </form>
  );
};

export default AddPayment;
